import React, { useState, useEffect } from 'react';
import './App.css';

interface Verse {
  verse: number;
  text: string;
}

interface Chapter {
  chapter: number;
  title: string;
  verses: Verse[];
}

interface BibleData {
  book: string;
  translation: string;
  style_note: string;
  chapters: Chapter[];
}

const BOOKS = [
  // Old Testament
  'Genesis', 'Exodus', 'Leviticus', 'Numbers', 'Deuteronomy',
  'Joshua', 'Judges', 'Ruth', '1 Samuel', '2 Samuel',
  '1 Kings', '2 Kings', '1 Chronicles', '2 Chronicles', 'Ezra',
  'Nehemiah', 'Esther', 'Job', 'Psalms', 'Proverbs',
  'Ecclesiastes', 'Song of Solomon', 'Isaiah', 'Jeremiah', 'Lamentations',
  'Ezekiel', 'Daniel', 'Hosea', 'Joel', 'Amos',
  'Obadiah', 'Jonah', 'Micah', 'Nahum', 'Habakkuk',
  'Zephaniah', 'Haggai', 'Zechariah', 'Malachi',
  // New Testament
  'Matthew', 'Mark', 'Luke', 'John', 'Acts',
  'Romans', '1 Corinthians', '2 Corinthians', 'Galatians', 'Ephesians',
  'Philippians', 'Colossians', '1 Thessalonians', '2 Thessalonians', '1 Timothy',
  '2 Timothy', 'Titus', 'Philemon', 'Hebrews', 'James',
  '1 Peter', '2 Peter', '1 John', '2 John', '3 John',
  'Jude', 'Revelation',
];

const AVAILABLE_BOOKS = ['Matthew'];

const TableOfContents: React.FC<{ onSelectBook: (book: string) => void }> = ({ onSelectBook }) => {
  const oldTestament = BOOKS.slice(0, 39);
  const newTestament = BOOKS.slice(39);

  return (
    <div className="toc">
      <header className="App-header">
        <img src="/thegreatone_b.png" alt="The Great One" className="header-logo header-logo-left" />
        <img src="/thegreatone_t.png" alt="The Great One" className="header-logo header-logo-right" />
        <h1>Excellent Bible</h1>
        <p className="translation">Be excellent to each other</p>
      </header>
      <div className="toc-content">
        <div className="toc-section">
          <h2 className="toc-heading">Old Testament</h2>
          <ul className="toc-list">
            {oldTestament.map((book) => (
              <li key={book}>
                {AVAILABLE_BOOKS.includes(book) ? (
                  <button className="toc-book available" onClick={() => onSelectBook(book)}>
                    {book}
                  </button>
                ) : (
                  <span className="toc-book unavailable">{book}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
        <div className="toc-section">
          <h2 className="toc-heading">New Testament</h2>
          <ul className="toc-list">
            {newTestament.map((book) => (
              <li key={book}>
                {AVAILABLE_BOOKS.includes(book) ? (
                  <button className="toc-book available" onClick={() => onSelectBook(book)}>
                    {book}
                  </button>
                ) : (
                  <span className="toc-book unavailable">{book}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const BookReader: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [data, setData] = useState<BibleData | null>(null);
  const [selectedChapter, setSelectedChapter] = useState<number>(1);

  useEffect(() => {
    fetch('/gospel-of-matthew.json')
      .then((response) => response.json())
      .then((json: BibleData) => setData(json));
  }, []);

  if (!data) return <div className="loading">Loading... Be excellent to each other!</div>;

  const chapter = data.chapters.find((c) => c.chapter === selectedChapter);

  return (
    <div className="App">
      <header className="App-header">
        <img src="/thegreatone_b.png" alt="The Great One" className="header-logo header-logo-left" />
        <img src="/thegreatone_t.png" alt="The Great One" className="header-logo header-logo-right" />
        <button className="back-btn" onClick={onBack}>← All Books</button>
        <h1>{data.book}</h1>
        <p className="translation">{data.translation}</p>
        <p className="style-note">"{data.style_note}"</p>
      </header>

      <nav className="chapter-nav">
        {data.chapters.map((c) => (
          <button
            key={c.chapter}
            className={`chapter-btn ${c.chapter === selectedChapter ? 'active' : ''}`}
            onClick={() => setSelectedChapter(c.chapter)}
          >
            {c.chapter}
          </button>
        ))}
      </nav>

      {chapter && (
        <main className="chapter-content">
          <h2 className="chapter-title">
            Chapter {chapter.chapter}: {chapter.title}
          </h2>
          <div className="verses">
            {chapter.verses.map((v) => (
              <p key={v.verse} className="verse">
                <span className="verse-number">{v.verse}</span>
                {v.text}
              </p>
            ))}
          </div>
        </main>
      )}
    </div>
  );
};

const App: React.FC = () => {
  const [selectedBook, setSelectedBook] = useState<string | null>(null);

  if (selectedBook) {
    return <BookReader onBack={() => setSelectedBook(null)} />;
  }

  return <TableOfContents onSelectBook={setSelectedBook} />;
};

export default App;
